<template>
  <div
      v-show="(isMobile && !showRoomsList) || !isMobile || singleRoom"
      class="vac-col-messages"
      @drop.prevent="onDropFiles"
      @dragenter.prevent
      @dragover.prevent
      @dragleave.prevent
      @touchstart="touchStart"
  >
    <slot v-if="showNoRoom" name="no-room-selected">
      <div class="vac-container-center vac-room-empty">
        <div>
          <svg t="1773673853584" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="8723" width="60" height="60"><path d="M512 85.333333a426.666667 426.666667 0 0 0-345.6 676.693334L128 878.933333a20.906667 20.906667 0 0 0 5.12 21.76 21.76 21.76 0 0 0 21.76 5.546667l121.6-39.253333A426.666667 426.666667 0 1 0 512 85.333333z m248.32 602.453334a119.893333 119.893333 0 0 1-85.333333 60.16c-22.186667 4.693333-51.626667 8.533333-149.333334-32a530.346667 530.346667 0 0 1-213.333333-187.733334 242.773333 242.773333 0 0 1-50.773333-128 136.96 136.96 0 0 1 42.666666-104.106666 63.573333 63.573333 0 0 1 42.666667-15.36h14.08c12.373333 0 18.773333 0 27.306667 20.906666s34.986667 85.333333 37.973333 92.16a24.32 24.32 0 0 1 2.133333 23.04 78.933333 78.933333 0 0 1-14.08 19.626667c-5.973333 7.253333-12.373333 12.8-18.346666 20.48a20.053333 20.053333 0 0 0-5.12 26.88 381.44 381.44 0 0 0 69.12 85.333333 313.173333 313.173333 0 0 0 100.266666 61.866667 26.453333 26.453333 0 0 0 29.866667-4.693333 426.666667 426.666667 0 0 0 33.28-42.666667 23.893333 23.893333 0 0 1 30.72-8.96c11.52 3.84 72.533333 34.133333 85.333333 40.106667s20.906667 9.386667 23.893334 14.506666a106.666667 106.666667 0 0 1-2.986667 58.453334z" p-id="8724" fill="#f0f0f0"></path></svg>
        </div>
      </div>
    </slot>

    <room-header
        v-else
        :current-user-id="currentUserId"
        :text-messages="textMessages"
        :single-room="singleRoom"
        :show-rooms-list="showRoomsList"
        :is-mobile="isMobile"
        :room-info-enabled="roomInfoEnabled"
        :menu-actions="menuActions"
        :room="room"
        :message-selection-enabled="messageSelectionEnabled"
        :message-selection-actions="messageSelectionActions"
        :selected-messages-total="selectedMessages.length"
        @toggle-rooms-list="$emit('toggle-rooms-list')"
        @room-info="$emit('room-info')"
        @menu-action-handler="$emit('menu-action-handler', $event)"
        @room-action-handler="$emit('room-action-handler', $event)"
        @message-selection-action-handler="messageSelectionActionHandler"
        @cancel-message-selection="messageSelectionEnabled = false"
        @show-right-info="handleClickShowRight"
        @close-room="$emit('close-room')"
    >
      <template v-for="(i, name) in $slots" #[name]="data">
        <slot :name="name" v-bind="data"/>
      </template>
    </room-header>

    <div
        id="messages-list"
        ref="scrollContainer"
        class="vac-container-scroll"
        @scroll="onContainerScroll"
    >
      <loader :show="loadingMessages" type="messages">
        <template v-for="(idx, name) in $slots" #[name]="data">
          <slot :name="name" v-bind="data"/>
        </template>
      </loader>
      <div class="vac-messages-container">
        <div class="vac-bg-layer"></div>
        <div :class="{ 'vac-messages-hidden': loadingMessages }">
          <transition name="vac-fade-message">
            <div>
              <div v-if="showNoMessages" class="vac-text-started">
                <slot name="messages-empty">
                  {{ textMessages.MESSAGES_EMPTY }}
                </slot>
              </div>
              <div v-if="showMessagesStarted" class="vac-text-started">
                {{ textMessages.CONVERSATION_STARTED }} {{ messages[0].date }}
              </div>
            </div>
          </transition>
          <div
              v-if="messages.length && !messagesLoaded"
              id="infinite-loader-messages"
          >
            <loader :show="true" :infinite="true" type="infinite-messages">
              <template v-for="(idx, name) in $slots" #[name]="data">
                <slot :name="name" v-bind="data"/>
              </template>
            </loader>
          </div>
          <transition-group :key="roomId" name="vac-fade-message" tag="span">
            <div
              v-for="(m, i) in messages"
              :key="m._id || m.id"
              :data-message-id="m?._id || m?.id || ''"
              :class="{ 'vac-message-locate-active': String(m?._id || m?.id || '') === String(highlightMessageId || '') }"
            >
              <room-message
                  :current-user-id="currentUserId"
                  :message="m"
                  :index="i"
                  :messages="messages"
                  :edited-message-id="editedMessageId"
                  :message-actions="messageActions"
                  :room-users="room.users"
                  :text-messages="textMessages"
                  :new-messages="newMessages"
                  :show-reaction-emojis="showReactionEmojis"
                  :show-new-messages-divider="showNewMessagesDivider"
                  :text-formatting="textFormatting"
                  :link-options="linkOptions"
                  :username-options="usernameOptions"
                  :message-selection-enabled="messageSelectionEnabled"
                  :selected-messages="selectedMessages"
                  :emoji-data-source="emojiDataSource"
                  @message-added="onMessageAdded"
                  @message-action-handler="messageActionHandler"
                  @open-file="openFile"
                  @open-user-tag="openUserTag"
                  @open-failed-message="$emit('open-failed-message', $event)"
                  @send-message-reaction="sendMessageReaction"
                  @select-message="selectMessage"
                  @unselect-message="unselectMessage"
              >
                <template v-for="(idx, name) in $slots" #[name]="data">
                  <slot :name="name" v-bind="data"/>
                </template>
              </room-message>
            </div>
          </transition-group>

          <div v-if="shouldShowLeaveGroupTip" class="vac-leave-group-tip">
            你已不再是该群组的成员，因此无法向该群组发送消息。
          </div>
        </div>
      </div>
    </div>
    <div v-if="!loadingMessages">
      <transition name="vac-bounce">
        <div v-if="scrollIcon" class="vac-icon-scroll" @click="scrollToBottom">
          <transition name="vac-bounce">
            <div
                v-if="scrollMessagesCount"
                class="vac-badge-counter vac-messages-count"
            >
              {{ scrollMessagesCount }}
            </div>
          </transition>
          <slot name="scroll-icon">
            <svg-icon name="dropdown" param="scroll"/>
          </slot>
        </div>
      </transition>
    </div>

    <room-footer
        v-if="!shouldHideRoomFooter"
        :room="room"
        :room-id="roomId"
        :room-message="roomMessage"
        :text-messages="textMessages"
        :show-send-icon="showSendIcon"
        :show-files="showFiles"
        :show-audio="showAudio"
        :show-emojis="showEmojis"
        :show-footer="showFooter"
        :accepted-files="acceptedFiles"
        :capture-files="captureFiles"
        :multiple-files="multipleFiles"
        :textarea-action-enabled="textareaActionEnabled"
        :textarea-auto-focus="textareaAutoFocus"
        :user-tags-enabled="userTagsEnabled"
        :emojis-suggestion-enabled="emojisSuggestionEnabled"
        :templates-text="templatesText"
        :text-formatting="textFormatting"
        :link-options="linkOptions"
        :audio-bit-rate="audioBitRate"
        :audio-sample-rate="audioSampleRate"
        :init-reply-message="initReplyMessage"
        :init-edit-message="initEditMessage"
        :dropped-files="droppedFiles"
        :emoji-data-source="emojiDataSource"
        @update-edited-message-id="editedMessageId = $event"
        @edit-message="$emit('edit-message', $event)"
        @send-message="$emit('send-message', $event)"
        @send-voice="$emit('send-voice', $event)"
        @typing-message="$emit('typing-message', $event)"
        @textarea-action-handler="$emit('textarea-action-handler', $event)"
    >
      <template v-for="(idx, name) in $slots" #[name]="data">
        <slot :name="name" v-bind="data"/>
      </template>
    </room-footer>
  </div>
</template>

<script>
import Loader from '@/modules/whatsapp/components/Loader/Loader.vue'
import SvgIcon from '@/modules/whatsapp/components/SvgIcon/SvgIcon.vue'
import RoomHeader from './RoomHeader/RoomHeader.vue'
import RoomFooter from './RoomFooter/RoomFooter.vue'
import RoomMessage from './RoomMessage/RoomMessage.vue'

export default {
  name: 'ChatRoom',
  components: {
    Loader,
    SvgIcon,
    RoomHeader,
    RoomFooter,
    RoomMessage
  },

  props: {
    currentUserId: {type: [String, Number], required: true},
    textMessages: {type: Object, required: true},
    singleRoom: {type: Boolean, required: true},
    showRoomsList: {type: Boolean, required: true},
    isMobile: {type: Boolean, required: true},
    rooms: {type: Array, required: true},
    roomId: {type: [String, Number], required: true},
    loadFirstRoom: {type: Boolean, required: true},
    messages: {type: Array, required: true},
    roomMessage: {type: String, default: null},
    messagesLoaded: {type: Boolean, required: true},
    menuActions: {type: Array, required: true},
    messageActions: {type: Array, required: true},
    messageSelectionActions: {type: Array, required: true},
    autoScroll: {type: Object, required: true},
    showSendIcon: {type: Boolean, required: true},
    showFiles: {type: Boolean, required: true},
    showAudio: {type: Boolean, required: true},
    audioBitRate: {type: Number, required: true},
    audioSampleRate: {type: Number, required: true},
    showEmojis: {type: Boolean, required: true},
    showReactionEmojis: {type: Boolean, required: true},
    showNewMessagesDivider: {type: Boolean, required: true},
    showFooter: {type: Boolean, required: true},
    acceptedFiles: {type: String, required: true},
    captureFiles: {type: String, required: true},
    multipleFiles: {type: Boolean, default: true},
    textFormatting: {type: Object, required: true},
    linkOptions: {type: Object, required: true},
    loadingRooms: {type: Boolean, required: true},
    roomInfoEnabled: {type: Boolean, required: true},
    textareaActionEnabled: {type: Boolean, required: true},
    textareaAutoFocus: {type: Boolean, required: true},
    userTagsEnabled: {type: Boolean, required: true},
    emojisSuggestionEnabled: {type: Boolean, required: true},
    scrollDistance: {type: Number, required: true},
    templatesText: {type: Array, default: null},
    usernameOptions: {type: Object, required: true},
    emojiDataSource: {type: String, default: undefined}
  },

  emits: [
    'toggle-rooms-list',
    'room-info',
    'show-right-info',
    'menu-action-handler',
    'room-action-handler',
    'message-selection-action-handler',
    'edit-message',
    'send-message',
    'send-voice',
    'delete-message',
    'message-action-handler',
    'fetch-messages',
    'send-message-reaction',
    'typing-message',
    'open-file',
    'open-user-tag',
    'open-failed-message',
    'textarea-action-handler',
    'close-room'
  ],

  data() {
    return {
      editedMessageId: null,
      initReplyMessage: null,
      initEditMessage: null,
      loadingMessages: false,
      observer: null,
      showLoader: true,
      loadingMoreMessages: false,
      scrollIcon: false,
      scrollMessagesCount: 0,
      suppressScrollIcon: false,
      newMessages: [],
      messageSelectionEnabled: false,
      selectedMessages: [],
      droppedFiles: [],
      highlightMessageId: null
    }
  },

  computed: {
    room() {
      // 如果 roomId 为空，直接返回空对象
      if (!this.roomId) return {}
      // 在 rooms 数组中查找
      const rid = String(this.roomId)
      const found = this.rooms.find(room => String(room?.roomId) === rid)
      // 如果找到则返回，否则返回一个包含基本数据的对象，避免 undefined 错误
      if (found) return found
      // 当房间不在 rooms 数组中时（如从 groupRooms 选择的群聊），
      // 返回一个包含基本 roomId 的对象，避免后续代码出错
      return {
        roomId: this.roomId,
        users: []
      }
    },
    isGroupRoom() {
      if (!this.room) return false
      if (this.room?.isGroup === true) return true
      if (this.room?.type === 'group') return true
      const rid = this.room?.roomId
      return typeof rid === 'string' ? rid.includes('@g.us') : false
    },
    hasLeftGroup() {
      const v = this.room?.leaveGroup
      if (v === true) return true
      if (v === 1) return true
      if (typeof v === 'string' && (v === 'true' || v === '1')) return true
      const raw = this.room?.raw
      const rv = raw?.leaveGroup
      if (rv === true) return true
      if (rv === 1) return true
      if (typeof rv === 'string' && (rv === 'true' || rv === '1')) return true
      return false
    },
    shouldHideRoomFooter() {
      return this.hasLeftGroup
    },
    shouldShowLeaveGroupTip() {
      if (!this.roomId) return false
      if (this.loadingRooms) return false
      return this.hasLeftGroup
    },
    showNoMessages() {
      return (
          this.roomId &&
          !this.messages.length &&
          !this.loadingMessages &&
          !this.loadingRooms
      )
    },
    showNoRoom() {
      const noRoomSelected =
          (!this.rooms.length && !this.loadingRooms) ||
          !this.roomId

      if (noRoomSelected) {
        this.updateLoadingMessages(false)
      }
      return noRoomSelected
    },
    showMessagesStarted() {
      return this.messages.length && this.messagesLoaded
    }
  },

  watch: {
    roomId: {
      immediate: true,
      handler() {
        this.onRoomChanged()
      }
    },
    messages: {
      deep: true,
      handler(newVal, oldVal) {
        newVal.forEach((message, i) => {
          if (
              this.showNewMessagesDivider &&
              !message.seen &&
              message.senderId !== this.currentUserId
          ) {
            this.newMessages.push({
              _id: message._id,
              index: i
            })
          }
        })
        if (oldVal?.length === newVal?.length - 1) {
          this.newMessages = []
          // Get the last added message
          const lastMessage = newVal[newVal.length - 1]
          // If message is sent by current user, scroll to bottom
          if (lastMessage && lastMessage.senderId === this.currentUserId) {
            this.scrollToBottom()
          }
        }
        setTimeout(() => (this.loadingMoreMessages = false))
      }
    },
    messagesLoaded(val) {
      if (val) this.updateLoadingMessages(false)
    }
  },

  mounted() {
    this.newMessages = []
  },

  methods: {
    locateMessageById(messageId) {
      const id = String(messageId || '').trim()
      if (!id) return false

      const container = this.$refs.scrollContainer
      if (!container || typeof container.querySelector !== 'function') return false

      const escaped = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        ? CSS.escape(id)
        : id.replace(/"/g, '\\"')
      const el = container.querySelector(`[data-message-id="${escaped}"]`)
      if (!el) {
        return false
      }

      this.highlightMessageId = id
      this.$nextTick(() => {
        try {
          const containerRect = container.getBoundingClientRect()
          const elRect = el.getBoundingClientRect()
          const delta = elRect.top - containerRect.top
          const targetTop = container.scrollTop + delta - 80
          container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })
        } catch (e) {
          container.scrollTop = Math.max(0, el.offsetTop - 80)
        }

        setTimeout(() => {
          if (String(this.highlightMessageId || '') === id) {
            this.highlightMessageId = null
          }
        }, 2000)
      })
      return true
    },
    //
    handleClickShowRight(val) {
      this.$emit("show-right-info", val)
    },
    //
    updateLoadingMessages(val) {
      this.loadingMessages = val

      if (!val) {
        setTimeout(() => this.initIntersectionObserver())
      }
    },
    initIntersectionObserver() {
      if (this.observer) {
        this.showLoader = true
        this.observer.disconnect()
      }

      const loader = this.$el.querySelector('#infinite-loader-messages')

      if (loader) {
        const options = {
          root: this.$el.querySelector('#messages-list'),
          rootMargin: `${this.scrollDistance}px`,
          threshold: 0
        }

        this.observer = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            this.loadMoreMessages()
          }
        }, options)

        this.observer.observe(loader)
      }
    },
    preventTopScroll() {
      const container = this.$refs.scrollContainer
      const prevScrollHeight = container.scrollHeight

      const observer = new ResizeObserver(_ => {
        if (container.scrollHeight !== prevScrollHeight) {
          if (this.$refs.scrollContainer) {
            this.$refs.scrollContainer.scrollTo({
              top: container.scrollHeight - prevScrollHeight
            })
            observer.disconnect()
          }
        }
      })

      for (var i = 0; i < container.children.length; i++) {
        observer.observe(container.children[i])
      }
    },
    touchStart(touchEvent) {
      if (this.singleRoom) return

      if (touchEvent.changedTouches.length === 1) {
        const posXStart = touchEvent.changedTouches[0].clientX
        const posYStart = touchEvent.changedTouches[0].clientY

        addEventListener(
            'touchend',
            touchEvent => this.touchEnd(touchEvent, posXStart, posYStart),
            {once: true}
        )
      }
    },
    touchEnd(touchEvent, posXStart, posYStart) {
      if (touchEvent.changedTouches.length === 1) {
        const posXEnd = touchEvent.changedTouches[0].clientX
        const posYEnd = touchEvent.changedTouches[0].clientY

        const swippedRight = posXEnd - posXStart > 100
        const swippedVertically = Math.abs(posYEnd - posYStart) > 50

        if (swippedRight && !swippedVertically) {
          this.$emit('toggle-rooms-list')
        }
      }
    },
    onRoomChanged() {
      // 如果 roomId 为空（关闭对话），重置 loadingMessages 并退出
      if (!this.roomId) {
        this.updateLoadingMessages(false)
        return
      }
      this.updateLoadingMessages(true)
      this.scrollIcon = false
      this.scrollMessagesCount = 0
      this.suppressScrollIcon = true
      this.resetMessageSelection()

      // 临时对话/空对话：如果上层已标记 messagesLoaded=true 且当前没有消息，
      // 不应该一直处于 loading（否则会卡在转圈状态）
      if (this.messagesLoaded && (!this.messages || this.messages.length === 0)) {
        this.updateLoadingMessages(false)
        return
      }

      const tryScrollToBottom = () => {
        this.$nextTick(() => {
          const element = this.$refs.scrollContainer
          if (!element) {
            this.updateLoadingMessages(false)
            return
          }

          const forceScrollToBottom = () => {
            element.scrollTop = element.scrollHeight
          }

          forceScrollToBottom()
          requestAnimationFrame(() => {
            forceScrollToBottom()
          })

          setTimeout(() => {
            forceScrollToBottom()
          }, 80)

          setTimeout(() => {
            forceScrollToBottom()
            this.updateLoadingMessages(false)
            this.suppressScrollIcon = false
          }, 200)
        })
      }

      const unwatch = this.$watch(
          () => (Array.isArray(this.messages) ? this.messages.length : 0),
          len => {
            if (!len) return
            if (!this.$refs.scrollContainer) return
            unwatch()
            tryScrollToBottom()
          },
          { immediate: true }
      )
    },
    resetMessageSelection() {
      this.messageSelectionEnabled = false
      this.selectedMessages = []
    },
    selectMessage(message) {
      if (!message) return
      const id = message?._id
      if (!id) return

      const list = Array.isArray(this.selectedMessages) ? this.selectedMessages : []
      const idx = list.findIndex((m) => String(m?._id) === String(id))
      if (idx > -1) {
        // toggle off
        list.splice(idx, 1)
        this.selectedMessages = list
        return
      }

      // toggle on
      this.selectedMessages = [...list, message]
    },
    unselectMessage(messageId) {
      this.selectedMessages = this.selectedMessages.filter(
          message => message._id !== messageId
      )
    },
    onMessageAdded({message, index, ref}) {
      if (index !== this.messages.length - 1) return

      if (this.suppressScrollIcon) {
        // During initial room switch / refresh auto-scroll, do not show scroll tip.
        return
      }

      const autoScrollOffset = ref.offsetHeight + 60

      setTimeout(() => {
        const scrollContainer = this.$refs.scrollContainer
        let scrolledUp = false

        if (scrollContainer) {
          scrolledUp = this.getBottomScroll(scrollContainer) > autoScrollOffset
        }
        
        // If message is sent by current user, scroll to bottom
        if (message.senderId === this.currentUserId) {
          this.scrollToBottom()
        } else {
          // Received message: scroll if already at bottom, otherwise show tip
          if (scrolledUp) {
            this.scrollIcon = true
            this.scrollMessagesCount++
          } else {
            this.scrollToBottom()
          }
        }
      })
    },
    onContainerScroll(e) {
      if (!e.target) return

      if (this.suppressScrollIcon) {
        // Ignore scroll computations while we are forcing scroll to bottom on refresh/room switch.
        return
      }

      const bottomScroll = this.getBottomScroll(e.target)
      if (bottomScroll < 60) this.scrollMessagesCount = 0
      this.scrollIcon = bottomScroll > 500 || this.scrollMessagesCount
    },
    loadMoreMessages() {
      if (this.loadingMessages) return

      setTimeout(
          () => {
            if (this.loadingMoreMessages) return

            if (this.messagesLoaded || !this.roomId) {
              this.loadingMoreMessages = false
              this.showLoader = false
              return
            }

            this.preventTopScroll()
            this.$emit('fetch-messages')
            this.loadingMoreMessages = true
          },
          // prevent scroll bouncing speed
          500
      )
    },
    messageActionHandler({action, message}) {
      switch (action.name) {
        case 'replyMessage':
          this.initReplyMessage = message
          setTimeout(() => {
            this.initReplyMessage = null
          })
          return
        case 'copyMessage':
          this.copyMessageContent(message)
          return
        case 'editMessage':
          this.initEditMessage = message
          setTimeout(() => {
            this.initEditMessage = null
          })
          return
        case 'deleteMessage':
          return this.$emit('delete-message', message)
        case 'selectMessages':
          this.messageSelectionEnabled = true
          // 默认选中当前消息，同时允许继续多选
          this.selectMessage(message)
          return
        default:
          return this.$emit('message-action-handler', {action, message})
      }
    },
    messageSelectionActionHandler(action) {
      this.$emit('message-selection-action-handler', {
        action,
        messages: this.selectedMessages
      })
      this.resetMessageSelection()
    },
    sendMessageReaction(messageReaction) {
      this.$emit('send-message-reaction', messageReaction)
    },
    getBottomScroll(element) {
      if (!element) return 0; // Return 0 if no container
      const {scrollHeight, clientHeight, scrollTop} = element
      return scrollHeight - clientHeight - scrollTop
    },
    scrollToBottom() {
      // 使用 requestAnimationFrame 确保流畅滚动
      requestAnimationFrame(() => {
        const element = this.$refs.scrollContainer
        if (!element) return
        
        // Scroll to bottom directly, no animation
        element.scrollTop = element.scrollHeight
      })
    },
    openFile({message, file}) {
      this.$emit('open-file', {message, file})
    },
    openUserTag(user) {
      this.$emit('open-user-tag', user)
    },
    onDropFiles(event) {
      if (this.showFiles) {
        this.droppedFiles = event.dataTransfer.files
      }
    },
    copyMessageContent(message) {
      const content = message.content || ''
      if (!content) {
        this.$message?.warning?.(this.$t('whatsapp.message.copyEmpty'))
        return
      }
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(content).then(() => {
          this.$message?.success?.(this.$t('whatsapp.message.copySuccess'))
        }).catch(() => {
          this.fallbackCopyText(content)
        })
      } else {
        this.fallbackCopyText(content)
      }
    },
    fallbackCopyText(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      textarea.style.top = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      
      try {
        const successful = document.execCommand('copy')
        if (successful) {
          this.$message?.success?.(this.$t('whatsapp.message.copySuccess'))
        } else {
          this.$message?.error?.(this.$t('whatsapp.message.copyFailed'))
        }
      } catch (err) {
        this.$message?.error?.(this.$t('whatsapp.message.copyFailed'))
      }
      
      document.body.removeChild(textarea)
    }
  }
}
</script>

<style lang="scss" scoped>
.vac-message-locate-active {
  background: rgba(255, 235, 59, 0.18);
}

.vac-leave-group-tip {
  padding: 16px 12px;
  color: #8c8c8c;
  font-size: 14px;
  text-align: center;
  background: rgba(243, 240, 235, 0.75);
}
</style>
